// Active-filter chip row + "Bộ lọc nâng cao" trigger.
//
// Each chip represents one filter currently applied to the URL. The
// row collapses to just the advanced-filter button when no filters
// are active. There are no predefined "quick filter" chips — to apply
// a filter the user opens the advanced modal.
//
// Filters that came from the AI extractor (URL has `?ai=1`) get a
// small sparkle indicator so the user knows they were inferred. The
// flag clears automatically when the last AI-applied chip is removed.

import { useState } from "react";
import { useSearchParams } from "react-router";
import { Sparkles, SlidersHorizontal, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { AdvancedFilterDialog } from "@/features/property/components/advanced-filter-dialog";
import { PropertyListingTypeToggle } from "@/features/property/components/property-listing-type-toggle";
import {
  AI_FILTER_KEYS,
  readFilterDraft,
} from "@/features/property/utils/property-filter-params";
import { wardLabel } from "@/features/property/utils/ward-options";

// ---------------------------------------------------------------------------
// Chip view-model
// ---------------------------------------------------------------------------

/**
 * One active filter chip. {@code key} matches the URL search-param key
 * we delete on remove. {@code fromAi} drives the sparkle indicator.
 */
interface ActiveChip {
  key: string;
  label: string;
  fromAi: boolean;
}

interface PropertyFiltersProps {
  className?: string;
}

export function PropertyFilters({ className }: PropertyFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const chips = buildActiveChips(searchParams);

  const removeChip = (key: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(key);
        // If removing this chip leaves zero AI-flagged filters, clear
        // the `ai` flag too so we don't claim AI is still influencing
        // the (now-empty) filter set.
        if ((AI_FILTER_KEYS as readonly string[]).includes(key)) {
          const stillHasAiKey = AI_FILTER_KEYS.some(
            (k) => k !== key && next.get(k) !== null && next.get(k) !== "",
          );
          if (!stillHasAiKey) next.delete("ai");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const clearAll = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const chip of chips) next.delete(chip.key);
        next.delete("ai");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <section
      aria-label="Bộ lọc bất động sản"
      className={cn("flex items-center gap-2", className)}
    >
      <PropertyListingTypeToggle className="shrink-0" />
      <div className="-mx-1 flex flex-1 items-center gap-1.5 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {chips.map((chip) => (
          <FilterChip
            key={chip.key}
            chip={chip}
            onRemove={() => removeChip(chip.key)}
          />
        ))}
        {chips.length > 1 ? (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-muted-foreground hover:text-foreground"
          >
            <X aria-hidden="true" className="size-3" />
            Bỏ tất cả
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => setAdvancedOpen(true)}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-ink-soft hover:bg-muted"
      >
        <SlidersHorizontal className="size-3" aria-hidden="true" />
        <span className="hidden sm:inline">Bộ lọc nâng cao</span>
      </button>
      <AdvancedFilterDialog open={advancedOpen} onOpenChange={setAdvancedOpen} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Individual chip
// ---------------------------------------------------------------------------

function FilterChip({
  chip,
  onRemove,
}: {
  chip: ActiveChip;
  onRemove: () => void;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] font-medium",
        chip.fromAi
          ? "border-primary/30 bg-primary/5 text-primary"
          : "border-border bg-card text-ink-soft",
      )}
    >
      {chip.fromAi ? (
        <Sparkles aria-hidden="true" className="size-3 shrink-0" />
      ) : null}
      <span>{chip.label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Bỏ lọc ${chip.label}`}
        className={cn(
          "-mr-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full transition-colors",
          chip.fromAi
            ? "text-primary/70 hover:bg-primary/10 hover:text-primary"
            : "text-ink-faint hover:bg-muted hover:text-ink-soft",
        )}
      >
        <X aria-hidden="true" className="size-3" />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Chip-list construction (pure)
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  AVAILABLE: "Đang trống",
  DEPOSIT: "Đặt cọc",
  SOLD: "Đã bán",
  RENTED: "Đã thuê",
};

function buildActiveChips(params: URLSearchParams): ActiveChip[] {
  const draft = readFilterDraft(params);
  const fromAi = params.get("ai") === "1";
  const chips: ActiveChip[] = [];

  // listingType has its own always-visible toggle (PropertyListingTypeToggle)
  // outside this chip row, so it's deliberately not duplicated here.
  if (draft.status !== undefined) {
    chips.push({
      key: "status",
      label: STATUS_LABELS[draft.status] ?? draft.status,
      fromAi,
    });
  }
  if (draft.wardCode !== undefined && draft.wardCode !== "") {
    chips.push({
      key: "wardCode",
      label: wardLabel(draft.wardCode),
      fromAi,
    });
  }
  if (draft.bedrooms !== undefined) {
    chips.push({ key: "bedrooms", label: `${draft.bedrooms} phòng ngủ`, fromAi });
  }
  if (draft.bathrooms !== undefined) {
    chips.push({ key: "bathrooms", label: `${draft.bathrooms} phòng tắm`, fromAi });
  }
  if (draft.areaMin !== undefined) {
    chips.push({ key: "areaMin", label: `≥ ${draft.areaMin} m²`, fromAi });
  }
  if (draft.areaMax !== undefined) {
    chips.push({ key: "areaMax", label: `≤ ${draft.areaMax} m²`, fromAi });
  }
  if (draft.priceMin !== undefined) {
    chips.push({
      key: "priceMin",
      label: `Giá từ ${formatPriceShort(draft.priceMin)}`,
      fromAi,
    });
  }
  if (draft.priceMax !== undefined) {
    chips.push({
      key: "priceMax",
      label: `Giá đến ${formatPriceShort(draft.priceMax)}`,
      fromAi,
    });
  }

  return chips;
}

function formatPriceShort(value: number): string {
  if (value >= 1_000_000_000) {
    const tier = value / 1_000_000_000;
    return `${tier % 1 === 0 ? tier : tier.toFixed(1)} tỷ`;
  }
  if (value >= 1_000_000) {
    const tier = value / 1_000_000;
    return `${tier % 1 === 0 ? tier : tier.toFixed(1)} triệu`;
  }
  return value.toLocaleString("vi-VN");
}
